// usePersonalGlobalActivityFeed.js

import { useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Services
import { subscribeToEngagements } from 'services/engagements/EngagementReadService';
import {
  getNodeBreadcrumbFields,
  subscribeToNodesByPersonKey,
  subscribeToUserNodes,
} from 'services/nodes/NodeReadService';
import { listHierarchyParentsForNodeKeys } from 'services/nodes/NodeEdgeService';
import {
  NODE_ACTIVITY_EVENTS_LIMIT_DEFAULT,
  subscribeToNodeActivityEvents,
} from 'services/nodes/NodeActivityService';

// Utilities
import { EngagementType } from 'utilities/Enums';
import {
  ACTIVITY_FEED_ITEM_KIND,
  buildActivityFeedItems,
} from 'node/activity/utils/buildActivityFeedItems';
import {
  collectProjectCandidateKeys,
  resolveActivityProjectContext,
} from 'home/activity/utils/resolveActivityProjectContext';
import { selectPersonalActivityNodes } from 'home/activity/utils/selectPersonalActivityNodes';
import { selectHomeActivityItems } from 'home/activity/utils/selectHomeActivityItems';
import {
  getActivityCommentKey,
  getActivityEventKey,
  indexActivityReactionsByCommentKey,
  indexActivityReactionsByEventKey,
} from 'node/activity/utils/activityReactions';
import useFavoriteNodes from 'favorites/hooks/useFavoriteNodes';

const EVENTS_PER_NODE = Math.max(20, NODE_ACTIVITY_EVENTS_LIMIT_DEFAULT);
const MAX_FEED_ITEMS = 80;

/**
 * usePersonalGlobalActivityFeed
 *
 * Merges activity from nodes the current user created or is assigned to.
 *
 * @param {object} params
 * @param {string} params.baseKey
 * @param {string} params.userKey
 * @param {boolean} [params.includeDetails]
 * @returns {{ items: object[], loading: boolean, error: string }}
 */
export default function usePersonalGlobalActivityFeed({
  baseKey = '',
  userKey = '',
  includeDetails = false,
}) {
  const { getType } = useNodeTypes();
  const { rows: favoriteNodes } = useFavoriteNodes();

  const [createdNodes, setCreatedNodes] = useState([]);
  const [assignedNodes, setAssignedNodes] = useState([]);
  const [loadingNodes, setLoadingNodes] = useState(true);
  const [eventsByNodeKey, setEventsByNodeKey] = useState({});
  const [comments, setComments] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [error, setError] = useState('');
  const [parentByChild, setParentByChild] = useState({});
  const [relatedNodesByKey, setRelatedNodesByKey] = useState(() => new Map());

  const cleanBaseKey = String(baseKey || '').trim();
  const cleanUserKey = String(userKey || '').trim();

  // Created nodes in this base.
  useEffect(() => {
    if (!cleanBaseKey || !cleanUserKey) {
      setCreatedNodes([]);
      setLoadingNodes(false);
      return undefined;
    }

    setLoadingNodes(true);
    return subscribeToUserNodes(cleanUserKey, (rows) => {
      const list = (Array.isArray(rows) ? rows : []).filter((node) => (
        String(node?.baseKey || '').trim() === cleanBaseKey
      ));
      setCreatedNodes(list);
      setLoadingNodes(false);
    }, { includeContributions: true });
  }, [cleanBaseKey, cleanUserKey]);

  // Assigned nodes via derived peopleKeys.
  useEffect(() => {
    if (!cleanBaseKey || !cleanUserKey) {
      setAssignedNodes([]);
      return undefined;
    }

    return subscribeToNodesByPersonKey({
      baseKey: cleanBaseKey,
      personKey: cleanUserKey,
    }, (rows) => {
      setAssignedNodes(Array.isArray(rows) ? rows : []);
    });
  }, [cleanBaseKey, cleanUserKey]);

  const favoriteKeys = useMemo(
    () => (Array.isArray(favoriteNodes) ? favoriteNodes : [])
      .map((node) => String(node?.key || '').trim())
      .filter(Boolean),
    [favoriteNodes]
  );

  const personalNodes = useMemo(() => (
    selectPersonalActivityNodes(
      [...createdNodes, ...assignedNodes, ...(favoriteNodes || [])],
      { pinnedKeys: favoriteKeys }
    )
  ), [assignedNodes, createdNodes, favoriteKeys, favoriteNodes]);

  const personalNodeKeys = useMemo(
    () => personalNodes.map((node) => String(node.key).trim()).filter(Boolean),
    [personalNodes]
  );

  const personalNodesRef = useRef(personalNodes);
  personalNodesRef.current = personalNodes;

  const personalNodesByKey = useMemo(() => {
    const map = new Map();
    personalNodes.forEach((node) => {
      if (node?.key) map.set(node.key, node);
    });
    relatedNodesByKey.forEach((node, key) => {
      if (!map.has(key)) map.set(key, node);
    });
    return map;
  }, [personalNodes, relatedNodesByKey]);

  const nodeKeysSignature = personalNodeKeys.join('|');

  // Hierarchical parents for project labels.
  useEffect(() => {
    const keys = nodeKeysSignature ? nodeKeysSignature.split('|') : [];
    if (!cleanBaseKey || keys.length === 0) {
      setParentByChild({});
      setRelatedNodesByKey(new Map());
      return undefined;
    }

    let cancelled = false;

    const loadParents = async () => {
      try {
        const firstParents = await listHierarchyParentsForNodeKeys(keys, {
          baseKey: cleanBaseKey,
        });
        if (cancelled) return;

        const parentKeys = Object.values(firstParents)
          .map((key) => String(key || '').trim())
          .filter(Boolean);
        const secondParents = parentKeys.length
          ? await listHierarchyParentsForNodeKeys(parentKeys, { baseKey: cleanBaseKey })
          : {};
        if (cancelled) return;

        const mergedParents = { ...secondParents, ...firstParents };
        setParentByChild(mergedParents);

        const relatedKeys = new Set(parentKeys);
        personalNodesRef.current.forEach((node) => {
          collectProjectCandidateKeys(node, mergedParents).forEach((key) => {
            relatedKeys.add(key);
          });
        });

        const crumbs = await getNodeBreadcrumbFields(Array.from(relatedKeys));
        if (cancelled) return;

        const next = new Map();
        crumbs.forEach((crumb, key) => {
          next.set(key, {
            key,
            title: crumb?.title || '',
            type: crumb?.type || '',
          });
        });
        setRelatedNodesByKey(next);
      } catch (err) {
        console.warn('[usePersonalGlobalActivityFeed] parent lookup failed:', err);
        if (!cancelled) {
          setParentByChild({});
        }
      }
    };

    loadParents();
    return () => {
      cancelled = true;
    };
  }, [cleanBaseKey, nodeKeysSignature]);

  // Per-node system activity.
  useEffect(() => {
    const keys = nodeKeysSignature ? nodeKeysSignature.split('|') : [];
    if (keys.length === 0) {
      setEventsByNodeKey({});
      setLoadingEvents(false);
      setError('');
      return undefined;
    }

    setLoadingEvents(true);
    setError('');
    const nextByKey = {};
    const ready = new Set();

    const unsubscribes = keys.map((nodeKey) => (
      subscribeToNodeActivityEvents(
        { nodeKey, limit: EVENTS_PER_NODE },
        (events) => {
          nextByKey[nodeKey] = Array.isArray(events) ? events : [];
          ready.add(nodeKey);
          setEventsByNodeKey({ ...nextByKey });
          if (ready.size >= keys.length) {
            setLoadingEvents(false);
          }
        },
        (err) => {
          console.warn('[usePersonalGlobalActivityFeed] activity subscribe failed:', err);
          nextByKey[nodeKey] = [];
          ready.add(nodeKey);
          setEventsByNodeKey({ ...nextByKey });
          setError('We could not load some recent activity.');
          if (ready.size >= keys.length) {
            setLoadingEvents(false);
          }
        }
      )
    ));

    return () => {
      unsubscribes.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
    };
  }, [nodeKeysSignature]);

  // Comments on the personal node set (same per-node subscribe as Activity).
  useEffect(() => {
    const keys = nodeKeysSignature ? nodeKeysSignature.split('|') : [];
    if (keys.length === 0) {
      setComments([]);
      setReactions([]);
      setLoadingComments(false);
      return undefined;
    }

    setLoadingComments(true);
    const commentsByKey = {};
    const reactionsByKey = {};
    const ready = new Set();

    const emit = () => {
      setComments(Object.values(commentsByKey).flat());
      setReactions(Object.values(reactionsByKey).flat());
      if (ready.size >= keys.length) {
        setLoadingComments(false);
      }
    };

    const unsubscribes = keys.map((nodeKey) => (
      subscribeToEngagements({
        params: [
          { field: 'itemKey', operator: '==', value: nodeKey },
        ],
        sortFields: [],
      }, (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        commentsByKey[nodeKey] = list.filter((row) => (
          row?.type === EngagementType.NODE_COMMENT
        ));
        reactionsByKey[nodeKey] = list.filter((row) => (
          row?.type === EngagementType.ACTIVITY_REACTION
          || row?.type === EngagementType.COMMENT_LIKE
        ));
        ready.add(nodeKey);
        emit();
      })
    ));

    return () => {
      unsubscribes.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
    };
  }, [nodeKeysSignature]);

  const commentsByNodeKey = useMemo(() => {
    const map = new Map();
    comments.forEach((comment) => {
      const nodeKey = String(comment?.itemKey || '').trim();
      if (!nodeKey) return;
      const list = map.get(nodeKey) || [];
      list.push(comment);
      map.set(nodeKey, list);
    });
    return map;
  }, [comments]);

  const reactionsByEventKey = useMemo(
    () => indexActivityReactionsByEventKey(reactions),
    [reactions]
  );

  const reactionsByCommentKey = useMemo(
    () => indexActivityReactionsByCommentKey(reactions),
    [reactions]
  );

  const items = useMemo(() => {
    const rows = [];

    personalNodes.forEach((node) => {
      const nodeKey = String(node?.key || '').trim();
      if (!nodeKey) return;

      const typeLabel = String(getType(node.type)?.label || '').trim();
      const nodeComments = commentsByNodeKey.get(nodeKey) || [];
      const nodeEvents = eventsByNodeKey[nodeKey] || [];
      const project = resolveActivityProjectContext({
        node,
        parentByChild,
        nodesByKey: personalNodesByKey,
      });

      buildActivityFeedItems({
        comments: nodeComments,
        events: nodeEvents,
        typeLabel,
      }).forEach((item) => {
        const eventKey = getActivityEventKey(item?.event);
        const commentKey = getActivityCommentKey(item?.comment);
        rows.push({
          ...item,
          node,
          nodeKey,
          projectLabel: project.label,
          projectNode: project.projectNode,
          nodeComments,
          reactions: eventKey
            ? (reactionsByEventKey.get(eventKey) || [])
            : (commentKey ? (reactionsByCommentKey.get(commentKey) || []) : []),
        });
      });
    });

    return selectHomeActivityItems(rows, { includeDetails }).slice(0, MAX_FEED_ITEMS);
  }, [
    commentsByNodeKey,
    eventsByNodeKey,
    reactionsByCommentKey,
    reactionsByEventKey,
    getType,
    includeDetails,
    parentByChild,
    personalNodes,
    personalNodesByKey,
  ]);

  return {
    items,
    loading: Boolean(cleanBaseKey && cleanUserKey) && (
      loadingNodes || loadingEvents || loadingComments
    ) && items.length === 0,
    error,
    ACTIVITY_FEED_ITEM_KIND,
  };
}
